
import Page from '@root/common/base/Page';
import GridSkeletonLoader from '@root/common/components/base/loader/GridSkeletonLoader.vue';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import { Data as Tag } from '@root/modules/tags/types/tag';
import { QueryResult } from '@root/modules/headlines/types/headlines';
import TagWithHeadlines from '@root/modules/tags/services/TagWithHeadlines.service';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import PageHeader from '@root/common/components/base/PageHeader.vue';
import getTagPageAdsSettings from '@root/modules/tags/utils/getTagPageAdsSettings';
import TagBreadcrumbs from '@root/modules/tags/components/TagBreadcrumbs.vue';
import TagDescription from '@root/modules/tags/components/TagDescription.vue';
import { sectionContentService } from '@root/modules/feed/services/sectionContent.service';
import ContentLayout from '@root/common/components/layout/ContentLayout.vue';
import HeadlineBlocks from '@root/common/components/base/headline/HeadlineBlocks.vue';
import { Section } from '@root/modules/frontpage/types/frontpage';
import getTagPath from '@root/modules/tags/utils/getTagPath';
import isMobileView from '@root/common/utils/isMobileView';

interface Data {
  tag: Tag | null;
  headlines: QueryResult;
  settings: {
    publishDate: boolean;
    titleComment: boolean;
    author: boolean;
  };
  isMobileView: boolean;
  sections: Section;
}

interface Methods {
  changePage: () => void;
  handleRedirect: () => void;
}

type Overrides = {
  slug: string;
  layout: string;
  description: string;
};

interface Computed {
  overrides: Overrides | undefined;
  layout: string;
  disabledAdsList: string[];
}

export default Page.extend<Data, Methods, Computed, unknown>({
  components: {
    HeadlineList,
    HeadlineBlocks,
    PageHeader,
    GridSkeletonLoader,
    AdsLayout,
    TagBreadcrumbs,
    TagDescription,
    ContentLayout,
  },
  data() {
    return {
      tag: null,
      headlines: {
        items: [],
        pager: {
          offset: 0,
          limit: 0,
        },
      },
      settings: { publishDate: true, titleComment: false, author: true },
      isMobileView: false,
      sections: [],
    };
  },
  computed: {
    overrides() {
      const { component } = this.$channelConfig('page').tags;

      return component?.overrides && component?.overrides.find((p) => p.slug === this.$route.params.slug);
    },
    layout() {
      return (this.overrides && this.overrides.layout) || 'BlockType101';
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
  },
  async fetch() {
    const slug = this.$route.params.slug;
    const page = this.$route.query.page as string;
    const { domain, lang, locale } = this.$channelConfig('settings');
    const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

    if (slug) {
      try {
        const tagWithHeadlines = new TagWithHeadlines({ req: this.$ssrContext?.req });
        const data = await tagWithHeadlines.fetch({ slug, language: lang.toUpperCase(), page });

        // Render 404 if tag is not found
        if (!data?.tag) {
          tagWithHeadlines.handleError({ message: 'tag.error.not_found', statusCode: 404 });
          return;
        }

        // Redirect to first page if no headlines found (404 fix)
        if (parseInt(page) > 1 && !data.headlines.items.length) {
          const tagUrl = this.$options.filters!.tagUrl({ tag: data.tag, locale, domain, fakeDomainEnabled });
          this.$nuxt.context.redirect(302, tagUrl);
          return;
        }

        // Redirect if tag has urlRedirect attribute value set
        if (data.tag.urlRedirect) {
          const urlRedirect = data?.tag.urlRedirect;
          this.$nuxt.context.redirect(302, urlRedirect);
          return;
        }

        // Extended Tag Content: check for pageSectionId attribute value
        if (data.tag.pageSectionId) {
          const pageSectionId = data.tag.pageSectionId;
          const variables = { id: pageSectionId };
          const response = await sectionContentService.fetch(variables);
          if (response && response.sections) {
            this.sections = response.sections;
          }
        }

        this.tag = data.tag;

        // Process tag headlines
        if (data.headlines.items.length) {
          this.headlines.items = data.headlines.items;

          if (data.headlines?.pager) {
            this.headlines.pager = data.headlines.pager;
          }
        }

        this.handleRedirect();

        this.setPageAdsSettings(getTagPageAdsSettings({ $channelConfig: this.$channelConfig, tag: this.tag }));
      } catch (e) {
        this.handlePageError(e);
      }
    }
  },
  mounted() {
    this.restoreScroll();

    this.isMobileView = isMobileView();
  },
  methods: {
    changePage() {
      this.$fetch();
    },
    handleRedirect() {
      if (!this.tag) {
        return;
      }

      const { domain, locale, lang } = this.$channelConfig('settings');

      const redirectPath = getTagPath(this.tag, {
        domain,
        locale,
        lang,
        routeId: this.$route.params.id,
        fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED,
        query: this.$nuxt.context.query,
        route: this.$nuxt.context.route,
      });

      this.handlePageRedirect(redirectPath);
    },
  },
  head() {
    const meta = this.getPageMeta();
    const query = this.$nuxt.context.query;

    const tagTitle = query?.page ? `${this.tag?.metaTitle || this.tag?.name}, ${this.$t('words.page')} ${query.page}` : this.tag?.metaTitle || this.tag?.name;
    const tagDescription = this.tag?.metaDescription ?? this.tag?.description;
    const tagKeywords = this.tag?.metaKeywords;

    if (tagTitle) {
      meta.setTitle({ title: tagTitle });
    }

    if (tagDescription) {
      meta.description = tagDescription;
      meta.ogDescription = tagDescription;
    }

    if (tagKeywords) {
      meta.keywords = tagKeywords;
    }

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
